import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import "./HouseTrainingPage.css";
import { ArrowLeft, Loader2 } from "lucide-react";
import dogLevel1 from "../../assets/dogLevel1.png";
import dogLevel2 from "../../assets/dogLevel2.png";
import dogLevel3 from "../../assets/dogLevel3.png";
import dogLevel4 from "../../assets/dogLevel4.png";
import nextIcon from "../../assets/nextButtonIcon2.png";
import nextIconMobile from "../../assets/nextButtonIcon.png";
import apiClient from "../../api/apiClient";
import { useGlobalAlert } from "../../context/AlertContext";
import TopNavBar from "../../components/TopNavBar";
import appIcon from "../../assets/appIcon.svg";
import socialPawsLogo from "../../assets/socialPawsLogo.svg";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";

const HouseTrainingPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const { triggerAlert } = useGlobalAlert();

  const { profile } = useSelector((state) => state.user);
  const [user, setUser] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!profile?.id) return;
      setPageLoading(true);
      try {
        const res = await apiClient.get(`/user/details/${profile?.id}`);
        if (res.data?.status && res.data.data) setUser(res.data.data);
      } catch (err) {
        console.error("Error fetching user:", err);
      } finally {
        setPageLoading(false);
      }
    };
    if (profile?.id) fetchUserDetails();
  }, [profile?.id]);

  const myDog = user?.dogs?.[0]?.dog_name || "My Dog";
  const dogId = user?.dogs?.[0]?.id || 0;

  useEffect(() => {
    const fetchPlaybook = async () => {
      if (!dogId) return;

      try {
        const res = await apiClient.get(
          `/pupCarePlaybook/get-pup-care-playbook-by-dogid/${dogId}`,
        );

        if (res.data?.status && res.data?.data) {
          setIsPlaybookExists(true);
        } else {
          setIsPlaybookExists(false);
        }
      } catch (err) {
        setIsPlaybookExists(false);
      }
    };

    if (dogId) {
      fetchPlaybook();
    }
  }, [dogId]);

  const sliderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const [dragPosition, setDragPosition] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPlaybookExists, setIsPlaybookExists] = useState(false);

  const [trainingLevel, setTrainingLevel] = useState(
    formData.houseTrainingLevel ?? 0,
  );
  const [isSpayed, setIsSpayed] = useState(formData.is_spayed ?? false);
  const [isMicrochipped, setIsMicrochipped] = useState(
    formData.is_microchipped ?? false,
  );

  useEffect(() => {
    if (formData.houseTrainingLevel !== undefined) {
      setTrainingLevel(formData.houseTrainingLevel);
      setIsSpayed(formData.is_spayed);
      setIsMicrochipped(formData.is_microchipped);
    }
  }, [formData]);

  const trainingSteps = [
    { label: "NOT YET", sub: "just starting out" },
    { label: "IN PROGRESS", sub: "currently being trained" },
    { label: "MOSTLY TRAINED", sub: "occasional accidents" },
    { label: "FULLY TRAINED", sub: "no accidents indoors" },
  ];

  const dogLevels = [dogLevel1, dogLevel2, dogLevel3, dogLevel4];
  const SLIDER_START = 5;
  const SLIDER_END = 95;
  const STEP_PERCENT = (SLIDER_END - SLIDER_START) / 3;

  const getPercentFromEvent = (clientX, clientY) => {
    if (!sliderRef.current) return SLIDER_START;
    const rect = sliderRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth <= 1023;

    let percent;
    if (isMobile) {
      // Vertical calculation (Top to Bottom)
      let y = clientY - rect.top;
      percent = 100 - (y / rect.height) * 100;
    } else {
      // Horizontal calculation (Left to Right)
      let x = clientX - rect.left;
      percent = (x / rect.width) * 100;
    }

    return Math.max(SLIDER_START, Math.min(percent, SLIDER_END));
  };

  const getStepFromPercent = (percent) => {
    return Math.min(
      Math.max(Math.round((percent - SLIDER_START) / STEP_PERCENT), 0),
      3,
    );
  };

  const handleDragStart = (e) => {
    e.stopPropagation();
    if (e.type === "mousedown" && e.button !== 0) return;
    isDraggingRef.current = true;
    setIsDragging(true);
    document.addEventListener("mousemove", handleDragMove);
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchmove", handleDragMove, { passive: false });
    document.addEventListener("touchend", handleDragEnd);
  };

  const handleDragMove = (e) => {
    if (!isDraggingRef.current) return;
    if (e.cancelable) e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const newPercent = getPercentFromEvent(clientX, clientY);
    setDragPosition(newPercent);
    const newStep = getStepFromPercent(newPercent);
    if (newStep !== trainingLevel) setTrainingLevel(newStep);
  };

  const handleDragEnd = () => {
    isDraggingRef.current = false;
    setIsDragging(false);
    setDragPosition(null);
    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", handleDragEnd);
    document.removeEventListener("touchmove", handleDragMove);
    document.removeEventListener("touchend", handleDragEnd);
  };

  const handleTrackClick = (e) => {
    const clientX = e.clientX;
    const clientY = e.clientY;
    if (clientX !== undefined && clientY !== undefined) {
      const newPercent = getPercentFromEvent(clientX, clientY);
      const newStep = getStepFromPercent(newPercent);
      setTrainingLevel(newStep);
    }
  };

  const handleDone = async () => {
    setIsSubmitting(true);
    const statusMap = [
      "Not yet",
      "In progress",
      "Mostly trained",
      "Fully trained",
    ];

    const finalVaccines = formData.vaccinations || [];

    const finalPayload = {
      dog_id: dogId,
      walk_guidelines: formData.walk_guidelines,
      leash_guidelines: formData.leash_guidelines,
      walkies_other_notes: formData.walkies_other_notes || "",
      treatos: formData.treatos,
      allergies_and_restrictions: formData.allergies_and_restrictions,
      meals: formData.meals,
      food_other_notes: formData.food_other_notes || "",
      vet_name: formData.vet_name,
      vet_phone: formData.vet_phone,
      vet_address: formData.vet_address || "",
      medical_conditions: formData.medical_conditions,
      medications_and_supplements: formData.medications_and_supplements || "",
      backup_name: formData.backup_name || "",
      backup_phone: formData.backup_phone || "",
      insurance_provider: formData.insurance_provider,
      insurance_phone: formData.insurance_phone || "",
      insurance_policy: formData.insurance_policy || "",
      vaccinations: finalVaccines,
      house_training_status: statusMap[trainingLevel],
      is_spayed: isSpayed,
      is_microchipped: isMicrochipped,
    };

    try {
      let res;
      if (isPlaybookExists) {
        res = await apiClient.put(
          `/pupCarePlaybook/update-pup-care-playbook-dogId/${dogId}`,
          finalPayload,
        );
      } else {
        res = await apiClient.post(
          `/pupCarePlaybook/create-pup-care-playbook`,
          finalPayload,
        );
      }

      if (res.data.status) {
        triggerAlert("Playbook Saved Successfully!", "success");
        navigate("/dashboard-switcher");
      }
    } catch (err) {
      console.error("Submission Error:", err);
      triggerAlert("Failed to save. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isMobile = window.innerWidth <= 1023;

  const basePos =
    isDragging && dragPosition !== null
      ? dragPosition
      : SLIDER_START + trainingLevel * STEP_PERCENT;

  const currentPos = isMobile ? 100 - basePos : basePos;

  const displaySteps = isMobile ? [...trainingSteps].reverse() : trainingSteps;

  if (pageLoading) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(157.14deg, #96f2de 2.76%, #58b8a3 96.76%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div className="hte-container">
      {/* Top Navbar (Desktop) */}
      <div className="hte-top-navbar hte-desktop-only">
        <TopNavBar theme="light" />
      </div>

      {/* Back Button (Desktop) */}
      <button
        className="hte-back-btn hte-desktop-only"
        style={{ top: "1.5rem", left: "1.5rem" }}
        onClick={() => navigate(-1)}
      >
        <ArrowLeft size={24} color="#000" strokeWidth={3} />
        <span style={{ color: "#000" }}>Back</span>
      </button>

      <div
        className="cb-custom-top-header cb-mobile-only"
        style={{ background: "transparent", width: "100%" }}
      >
        {/* TOP ROW */}
        <div className="cb-header-top-row" style={{ width: "100%" }}>
          <MobileTopBar
            onBack={() => navigate("/pup-emergency-plan")}
            backColor="#000"
            logoColor="#009fd9"
            blueLogo={true}
          />
        </div>

        {/* BOTTOM ROW */}
        <div className="cb-header-bottom-row" style={{ width: "100%" }}>
          <div
            className="header-icons-right"
            style={{ width: "100%", justifyContent: "space-between" }}
          >
            <TopNavBar theme="light" />
          </div>
        </div>
      </div>

      <div className="hte-content">
        <h1 className="hte-title">
          WHAT IS {myDog.toUpperCase()}'S HOUSE TRAINING STATUS?
        </h1>

        {/* Custom Slider */}
        <div
          className="hte-slider-wrapper"
          ref={sliderRef}
          onClick={handleTrackClick}
          style={{ "--pos": `${currentPos}%` }}
        >
          <div className="hte-track-bg"></div>
          <div
            className="hte-track-fill"
            style={{
              transition: isDragging ? "none" : "all 0.3s",
            }}
          />
          {displaySteps.map((step, index) => {
            const stepPos = SLIDER_START + index * STEP_PERCENT;
            const actualIndex = isMobile
              ? trainingSteps.length - 1 - index
              : index;
            const isActive = actualIndex === trainingLevel;

            return (
              <div
                key={index}
                className={`hte-step-container ${index % 2 === 0 ? "left-label" : "right-label"}`}
                style={{ "--step-pos": `${stepPos}%` }}
                onClick={(e) => {
                  e.stopPropagation();
                  setTrainingLevel(actualIndex);
                }}
              >
                <div className="hte-dot" />

                <div
                  className={`hte-label-group ${isActive ? "active-text" : ""}`}
                >
                  <span className="hte-label-main">{step.label}</span>
                  <span className="hte-label-sub">{step.sub}</span>
                </div>
              </div>
            );
          })}
          <div
            className={`hte-thumb-dog ${isDragging ? "dragging" : ""}`}
            style={{
              transition: isDragging ? "none" : "all 0.3s",
              left:
                window.innerWidth <= 1023
                  ? trainingLevel <= 1
                    ? "50%"
                    : "50%"
                  : undefined,
            }}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
          >
            <img
              src={dogLevels[trainingLevel]}
              alt="Dog"
              draggable="false"
              className={`hte-dog-level-icon ${trainingLevel >= 2 ? "hte-dog-level-large" : ""}`}
            />
          </div>
        </div>

        <h2 className="hte-health-section-title">Is {myDog}</h2>
        <div className="hte-health-toggles-row">
          <div className="hte-toggle-group">
            <span className="hte-toggle-label-text">SPAYED / NEUTERED?</span>
            <div
              className={`hte-dog-toggle-switch ${isSpayed ? "on" : "off"}`}
              onClick={() => setIsSpayed(!isSpayed)}
            >
              <div className="hte-toggle-track" />
              <div className="hte-toggle-thumb">
                <img
                  src={isSpayed ? dogLevel3 : dogLevel1}
                  alt="Toggle"
                  style={{ width: isSpayed ? "75px" : "65px" }}
                />
              </div>
            </div>
          </div>
          <div className="hte-toggle-group">
            <span className="hte-toggle-label-text">MICROCHIPPED?</span>
            <div
              className={`hte-dog-toggle-switch ${isMicrochipped ? "on" : "off"}`}
              onClick={() => setIsMicrochipped(!isMicrochipped)}
            >
              <div className="hte-toggle-track" />
              <div className="hte-toggle-thumb">
                <img
                  src={isMicrochipped ? dogLevel3 : dogLevel1}
                  alt="Toggle"
                  style={{ width: isMicrochipped ? "75px" : "65px" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="hte-footer desktop-only">
        <button
          className={`hte-next-btn-group ${isSubmitting ? "disabled" : "enabled"}`}
          onClick={!isSubmitting ? handleDone : undefined}
        >
          <span className="hte-next-text">
            {isSubmitting ? "SAVING..." : "DONE"}
          </span>
          <div className="hte-next-circle">
            {isSubmitting ? (
              <Loader2 className="animate-spin" color="#fff" />
            ) : (
              <img
                src={window.innerWidth <= 1023 ? nextIconMobile : nextIcon}
                alt="Done"
              />
            )}
          </div>
        </button>
      </div>
      <MobileBottomBar
        onNext={handleDone}
        isNextEnabled={!isSubmitting}
        nextIconSrc={nextIcon}
        nextText="DONE"
        variant="blue"
      />
    </div>
  );
};

export default HouseTrainingPage;
